<script setup lang="ts">
    import BaseIcon from '@/components/BaseIcon.vue';
    import BaseIconButton from '@/components/BaseIconButton.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import { EAppStep } from '@/typescript/enums/EAppStep';
    import useConfigStore from '@/store/config.store';
    import useProductStore from '@/store/product.store';
    import AbstractInput from '@/components/AbstractInput.vue';
    import NewFieldSelector from '@/components/NewFieldSelector.vue';
    import useStepper from '@/composables/useStepper';
    import BackgroundColorPicker from '@/components/BackgroundColorPicker.vue';
    import BaseInput from '@/components/BaseInput.vue';
    import AspectSelector from '@/components/AspectSelector.vue';
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, toRefs, useTemplateRef } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useScroll } from '@vueuse/core';

    const { t } = useI18n();

    const { isMobile, isAdmin, showAspectSelector } = storeToRefs(useConfigStore());
    const { selectedProductConfiguration, selectedProduct } = storeToRefs(useProductStore());
    const { goToStep } = useStepper();

    const fieldContainerRef = useTemplateRef('fieldContainer');
    const { arrivedState } = useScroll(fieldContainerRef);
    const { bottom } = toRefs(arrivedState);

    const shouldDisplayScrollBottom = computed(() => {
        return !bottom.value;
    });

    const filteredFields = computed(() => {
        if (isAdmin.value) {
            return selectedProductConfiguration.value.fields;
        }
        return selectedProductConfiguration.value.fields.filter((field) => field.editable);
    });

    const name = computed({
        get() {
            return selectedProduct.value.name;
        },
        async set(newName: string) {
            selectedProduct.value.name = newName;
        },
    });

    const isBackgroudColorPickerVisible = computed(() => {
        return isAdmin.value || selectedProductConfiguration.value.isPoleColorEditable;
    });

    onMounted(() => {
        if (!selectedProduct.value.prestashopUserId) {
            selectedProduct.value.name = t('configuration_name_default');
        }
    });

    function goToTemplateStep() {
        goToStep(EAppStep.TEMPLATES);
    }

    function goToPreviewStep() {
        goToStep(EAppStep.PREVIEW);
    }

    function scrollToBottom() {
        fieldContainerRef.value?.scrollTo(0, fieldContainerRef.value.scrollHeight);
    }
</script>

<template>
    <div class="relative flex h-full w-full flex-col gap-2 p-4">
        <div
            v-if="isMobile"
            id="formContainer"
            class="pointer-events-none absolute left-0 top-0 z-10 h-full w-full"
        ></div>
        <BaseButton
            v-if="!isAdmin"
            variant="text"
            :default-classes="false"
            class="flex flex-row items-center gap-1 border-transparent bg-transparent font-bebas-neue text-xl text-text"
            @click="goToTemplateStep"
        >
            <BaseIcon
                name="ChevronLeftIcon"
                size="lg"
            />
            {{ $t('back_to_template_list') }}
        </BaseButton>
        <div
            ref="fieldContainer"
            class="flex w-full grow flex-col gap-2 overflow-y-auto p-2"
        >
            <BaseInput
                v-model="name"
                :show-label="false"
                name="name"
                :label="$t('name')"
                class="h-10 w-full border border-transparent bg-white p-1 text-lg font-bold text-text"
            >
                <template #default="{ inputRef }">
                    <BaseIconButton
                        icon-name="PencilIcon"
                        :label="$t('edit_name')"
                        @click="inputRef?.focus()"
                    />
                </template>
            </BaseInput>
            <AspectSelector v-if="showAspectSelector" />
            <BackgroundColorPicker
                v-if="isBackgroudColorPickerVisible"
                class=""
            />
            <NewFieldSelector />
            <AbstractInput
                v-for="field in filteredFields"
                :key="field.id"
                :field
            />
        </div>

        <BaseButton
            v-if="isMobile"
            class="w-full flex-none self-end"
            @click="goToPreviewStep"
        >
            {{ $t('show_preview_label') }}
        </BaseButton>

        <BaseButton
            v-if="shouldDisplayScrollBottom"
            variant="filled"
            class="fixed bottom-16 left-1/2 h-12 w-12 -translate-x-1/2 rounded-full"
            @click="scrollToBottom"
        >
            <BaseIcon
                name="ChevronDownIcon"
                class="h-6 w-6"
            />
        </BaseButton>
    </div>
</template>
